// Täckningsvy: vilka dokument och steg i A–Ö-processen som saknas i projektet,
// utifrån den detaljnivå som gäller. Läser befintlig data – inga nya tabeller.
import { supabase } from "@/integrations/supabase/client";
import { PROCESS_STEPS, PROCESS_PHASES, type ProcessPhase, type ProcessStepDef } from "./constants";
import {
  ALWAYS_REQUIRED_DOCS,
  DOCS_BY_LEVEL,
  fetchDetailLevel,
  type DetailLevel,
  type DetailLevelState,
} from "./detailLevel";

export type CoverageStatus = "missing" | "in_progress" | "done" | "not_applicable";

export interface CoverageItem {
  docNumber: string;
  title: string;
  phase: ProcessPhase;
  owner: ProcessStepDef["owner"];
  afs: string[];
  appLink?: ProcessStepDef["appLink"];
  status: CoverageStatus;
  /** Ingår i vald detaljnivå. */
  inLevel: boolean;
  /** Måste finnas oavsett nivå. */
  alwaysRequired: boolean;
  /** Motivering när steget är markerat som ej aktuellt. */
  notApplicableReason: string | null;
}

export interface PhaseCoverage {
  key: ProcessPhase;
  label: string;
  items: CoverageItem[];
  total: number;
  done: number;
}

export interface CoverageResult {
  level: DetailLevel;
  detail: DetailLevelState;
  items: CoverageItem[];
  /** Steg som är aktuella på nivån. */
  applicable: CoverageItem[];
  missing: CoverageItem[];
  inProgress: CoverageItem[];
  done: CoverageItem[];
  notApplicable: CoverageItem[];
  /** Alltid-krav som saknas – visas överst med varning. */
  criticalMissing: CoverageItem[];
  percent: number;
  byPhase: PhaseCoverage[];
  owners: string[];
}

const ALWAYS = new Set<string>(ALWAYS_REQUIRED_DOCS as readonly string[]);

const normalizeStatus = (raw: string | null | undefined): CoverageStatus => {
  if (raw === "done" || raw === "completed") return "done";
  if (raw === "in_progress") return "in_progress";
  if (raw === "not_applicable") return "not_applicable";
  return "missing";
};

/** Läser projektets täckning mot hela processen. */
export const fetchCoverage = async (projectId: string): Promise<CoverageResult> => {
  const [detail, stepsRes] = await Promise.all([
    fetchDetailLevel(projectId),
    supabase
      .from("process_steps")
      .select("doc_number, status, not_applicable, not_applicable_reason")
      .eq("project_id", projectId),
  ]);

  const rows = (stepsRes.data || []) as Array<{
    doc_number: string;
    status: string | null;
    not_applicable: boolean | null;
    not_applicable_reason: string | null;
  }>;
  const byDoc = new Map(rows.map((r) => [r.doc_number, r]));

  const inLevelSet = new Set(DOCS_BY_LEVEL[detail.level]);

  const items: CoverageItem[] = PROCESS_STEPS.map((step) => {
    const row = byDoc.get(step.docNumber);
    const inLevel = inLevelSet.has(step.docNumber);
    let status = normalizeStatus(row?.status);
    if (row?.not_applicable) status = "not_applicable";
    // Steg utanför nivån räknas som ej aktuella även om raden saknas.
    if (!inLevel && status === "missing") status = "not_applicable";

    return {
      docNumber: step.docNumber,
      title: step.title,
      phase: step.phase,
      owner: step.owner,
      afs: step.afs ?? [],
      appLink: step.appLink,
      status,
      inLevel,
      alwaysRequired: ALWAYS.has(step.docNumber),
      notApplicableReason:
        status === "not_applicable"
          ? row?.not_applicable_reason ??
            (inLevel ? null : `Ingår inte i detaljnivå "${detail.level}".`)
          : null,
    };
  });

  const applicable = items.filter((i) => i.status !== "not_applicable");
  const missing = applicable.filter((i) => i.status === "missing");
  const inProgress = applicable.filter((i) => i.status === "in_progress");
  const done = applicable.filter((i) => i.status === "done");
  const notApplicable = items.filter((i) => i.status === "not_applicable");
  const criticalMissing = items.filter(
    (i) => i.alwaysRequired && i.status !== "done" && i.status !== "in_progress"
  );

  const byPhase: PhaseCoverage[] = PROCESS_PHASES.map((p) => {
    const phaseItems = applicable.filter((i) => i.phase === p.key);
    return {
      key: p.key,
      label: p.label,
      items: phaseItems,
      total: phaseItems.length,
      done: phaseItems.filter((i) => i.status === "done").length,
    };
  });

  return {
    level: detail.level,
    detail,
    items,
    applicable,
    missing,
    inProgress,
    done,
    notApplicable,
    criticalMissing,
    percent: applicable.length === 0 ? 100 : Math.round((done.length / applicable.length) * 100),
    byPhase,
    owners: Array.from(new Set(PROCESS_STEPS.map((s) => s.owner))).sort(),
  };
};
